import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { useAuth } from './AuthContext';
import { LogIn, LogOut, User, Loader2 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

// Emergent OAuth URL
const OAUTH_URL = process.env.REACT_APP_OAUTH_URL;

const LoginButton = ({ onSettingsClick }) => {
  const { user, isAuthenticated, loading, logout, tier } = useAuth();
  const [loggingOut, setLoggingOut] = useState(false);

  const handleLogin = () => {
    // Redirect to Emergent OAuth
    const redirectUrl = `${window.location.origin}/auth/callback`;
    window.location.href = `${OAUTH_URL}?redirect_url=${encodeURIComponent(redirectUrl)}`;
  };

  const handleLogout = async () => {
    setLoggingOut(true);
    await logout();
    setLoggingOut(false);
  };

  if (loading) {
    return (
      <Button variant="ghost" disabled size="sm">
        <Loader2 className="w-4 h-4 animate-spin" />
      </Button>
    );
  }

  if (!isAuthenticated) {
    return (
      <Button onClick={handleLogin} size="sm" variant="outline">
        <LogIn className="w-4 h-4 mr-2" />
        Sign In
      </Button>
    );
  }

  const tierColors = {
    free: 'bg-gray-500',
    pro: 'bg-blue-500',
    team: 'bg-purple-500',
    enterprise: 'bg-amber-500',
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="flex items-center space-x-2">
          <Avatar className="h-6 w-6">
            <AvatarImage src={user.picture} alt={user.name} />
            <AvatarFallback>{user.name?.[0]?.toUpperCase() || 'U'}</AvatarFallback>
          </Avatar>
          <span className="text-sm hidden md:inline">{user.name}</span>
          <Badge className={`${tierColors[tier]} text-[10px] h-4 px-1.5`}>
            {tier.toUpperCase()}
          </Badge>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <div className="px-2 py-1.5">
          <p className="text-sm font-medium">{user.name}</p>
          <p className="text-xs text-gray-500">{user.email}</p>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onSettingsClick}>
          <User className="w-4 h-4 mr-2" />
          Account Settings
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleLogout} disabled={loggingOut}>
          {loggingOut ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <LogOut className="w-4 h-4 mr-2" />
          )}
          Sign Out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LoginButton;
